import React, {useEffect, useMemo, useRef} from "react"
import PropTypes from "prop-types"
import {Doughnut} from "react-chartjs-2"
import {ArcElement, Chart as ChartJS, Legend, Tooltip} from "chart.js"
import {createCenterTextPlugin} from "./plugins/centerText"
import {maxRingThicknessPlugin} from "./plugins/maxRingThickness"
import {ringSpacingPlugin} from "./plugins/ringSpacing"
import {singleSegmentBorderPlugin} from "./plugins/singleSegmentBorder"

ChartJS.register(ArcElement, Tooltip, Legend)

const options = {
    plugins: {
        legend: {
            display: false,
        },
        tooltip: {
            callbacks: {
                label: (tooltipItem) => {
                    const item = tooltipItem.dataset
                    const name = item.segmentLabels?.[tooltipItem.dataIndex] ?? ''
                    const formattedValue = item?.formattedValue?.[tooltipItem.dataIndex] ?? tooltipItem.formattedValue

                    return name ? ` ${name}: ${formattedValue}` : ` ${formattedValue}`
                }
            },
        },
    },
    animation: false,
    elements: {
        arc: {
            hoverOffset: 0,
            hoverBorderWidth: 0,
        },
    },
    responsive: true,
    maintainAspectRatio: false,
}

const StackedDonutBase = ({
                              centerTitle = "Title",
                              centerSubtitle = "Subtitle",
                              height = 250,
                              datasets,
                              labels
                          }) => {
    const chartRef = useRef(null)

    const chartData = useMemo(
        () => ({
            labels,
            datasets,
        }),
        [datasets],
    )

    useEffect(() => {
        const chart = chartRef.current
        if (!chart) return
        datasets.forEach((_, i) => chart.setDatasetVisibility(i, true))
        chart.update("none")
    }, [datasets.length])

    const centerTextValues = useRef({title: centerTitle, subtitle: centerSubtitle})

    useEffect(() => {
        centerTextValues.current.title = centerTitle
        centerTextValues.current.subtitle = centerSubtitle
        chartRef.current?.update("none")
    }, [centerTitle, centerSubtitle])

    const centerTextPlugin = useMemo(() => createCenterTextPlugin(centerTextValues.current), [])

    return (
        <div style={{position: "relative", width: "100%", height}}>
            <Doughnut
                ref={chartRef}
                data={chartData}
                options={options}
                plugins={[
                    singleSegmentBorderPlugin,
                    maxRingThicknessPlugin,
                    ringSpacingPlugin,
                    centerTextPlugin,
                ]}
            />
        </div>
    )
}

StackedDonutBase.propTypes = {
    centerTitle: PropTypes.string,
    centerSubtitle: PropTypes.string,
    height: PropTypes.number,
    labels: PropTypes.arrayOf(PropTypes.string),
    datasets: PropTypes.arrayOf(
        PropTypes.shape({
            label: PropTypes.string,
            segmentLabels: PropTypes.arrayOf(PropTypes.string).isRequired,
            data: PropTypes.arrayOf(PropTypes.number).isRequired,
            backgroundColor: PropTypes.arrayOf(PropTypes.string).isRequired,
            formattedValue: PropTypes.arrayOf(PropTypes.string),
        }),
    ).isRequired,
}

StackedDonutBase.defaultProps = {
    centerTitle: "",
    centerSubtitle: "",
    height: 250,
}

export default StackedDonutBase